import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CCard, CCardBody, CButton, CFormInput, CInputGroup,
  CTable, CTableHead, CTableRow, CTableHeaderCell, CTableBody, CTableDataCell,
  CSpinner, CAlert, CPagination, CPaginationItem, CRow, CCol,
} from '@coreui/react';
import CIcon from '@coreui/icons-react';
import { cilPencil, cilSearch, cilX, cilUserPlus, cilPeople } from '@coreui/icons';
import api from '../services/api';
import SortableHeader from '../components/SortableHeader';

function PaginationBar({ page, totalPages, setPage }) {
  const [goTo, setGoTo] = useState('');
  const pages = [];
  if (totalPages <= 9) {
    for (let i = 1; i <= totalPages; i++) pages.push(i);
  } else {
    [1, 2, 3].forEach(p => pages.push(p));
    if (page > 5) pages.push({ ellipsis: true, key: 'left' });
    for (let i = Math.max(4, page - 1); i <= Math.min(totalPages - 3, page + 1); i++) pages.push(i);
    if (page < totalPages - 4) pages.push({ ellipsis: true, key: 'right' });
    [totalPages - 2, totalPages - 1, totalPages].forEach(p => pages.push(p));
  }
  const handleGoTo = (e) => {
    e.preventDefault();
    const n = parseInt(goTo, 10);
    if (n >= 1 && n <= totalPages) { setPage(n); setGoTo(''); }
  };
  return (
    <div className="d-flex align-items-center gap-3 flex-wrap mt-3">
      <CPagination className="mb-0">
        <CPaginationItem disabled={page === 1} onClick={() => setPage(p => p - 1)}>‹</CPaginationItem>
        {pages.map((p) =>
          p.ellipsis
            ? <CPaginationItem key={p.key} disabled>…</CPaginationItem>
            : <CPaginationItem key={p} active={p === page} onClick={() => setPage(p)}>{p}</CPaginationItem>
        )}
        <CPaginationItem disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>›</CPaginationItem>
      </CPagination>
      <div className="d-flex align-items-center gap-2">
        <small className="text-muted">Page {page} of {totalPages}</small>
        <span className="text-muted">·</span>
        <form onSubmit={handleGoTo} className="d-flex align-items-center gap-1">
          <span className="text-muted small">Go to</span>
          <input type="number" min={1} max={totalPages} value={goTo} onChange={e => setGoTo(e.target.value)}
            placeholder="#" style={{ width: 56 }} className="form-control form-control-sm text-center" />
          <button type="submit" className="btn btn-sm btn-outline-secondary px-2">→</button>
        </form>
      </div>
    </div>
  );
}

export default function CustomersPage() {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [sortBy, setSortBy] = useState('date');
  const [sortDir, setSortDir] = useState('desc');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const pageSize = 20;
  const totalPages = Math.ceil(total / pageSize);

  function handleSort(col) {
    if (col === sortBy) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortBy(col); setSortDir('asc'); }
    setPage(1);
  }

  const fetchCustomers = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await api.get('/customers', { params: { page, search, sortBy, sortDir } });
      setCustomers(data.customers);
      setTotal(data.total);
    } catch {
      setError('Failed to load customers.');
    } finally {
      setLoading(false);
    }
  }, [page, search, sortBy, sortDir]);

  useEffect(() => { fetchCustomers(); }, [fetchCustomers]);

  function handleSearch(e) {
    e.preventDefault();
    setPage(1);
    setSearch(searchInput);
  }

  return (
    <>
      <CRow className="mb-3 align-items-center">
        <CCol>
          <h5 className="mb-0">Customers</h5>
        </CCol>
        <CCol xs="auto">
          <CButton color="success" size="sm" onClick={() => navigate('/customers/new')}>
            <CIcon icon={cilUserPlus} className="me-1" /> Add Customer
          </CButton>
        </CCol>
      </CRow>

      <CCard>
        <CCardBody>
          <form onSubmit={handleSearch} className="d-flex gap-2 mb-3">
            <CInputGroup>
              <CFormInput
                placeholder="Search by name or phone…"
                value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
              />
              <CButton type="submit" color="secondary">
                <CIcon icon={cilSearch} />
              </CButton>
              {search && (
                <CButton color="secondary" variant="outline" onClick={() => { setSearch(''); setSearchInput(''); setPage(1); }}>
                  <CIcon icon={cilX} />
                </CButton>
              )}
            </CInputGroup>
          </form>

          {error && <CAlert color="danger">{error}</CAlert>}

          {loading ? (
            <div className="text-center py-4"><CSpinner color="success" /></div>
          ) : (
            <>
              <div className="mb-2">
                <small className="text-muted d-flex align-items-center gap-1">
                  <CIcon icon={cilPeople} size="sm" className="text-success" />
                  {search ? <><strong>{total}</strong> result{total !== 1 ? 's' : ''} for "<em>{search}</em>"</> : <><strong>{total}</strong> customer{total !== 1 ? 's' : ''}</>}
                </small>
              </div>

              <CTable hover responsive className="mb-0">
                <CTableHead color="light">
                  <CTableRow>
                    <SortableHeader col="nama"   label="Name"     sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                    <SortableHeader col="telhp"  label="Phone"    sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                    <SortableHeader col="sykt"   label="Company"  sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                    <SortableHeader col="daerah" label="District" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                    <SortableHeader col="date"   label="Date"     sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                    <CTableHeaderCell style={{ width: 60 }}></CTableHeaderCell>
                  </CTableRow>
                </CTableHead>
                <CTableBody>
                  {customers.map(c => (
                    <CTableRow key={c.user_id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/customers/${c.uid}/edit`)}>
                      <CTableDataCell>{c.nama}</CTableDataCell>
                      <CTableDataCell>{c.telhp}</CTableDataCell>
                      <CTableDataCell>{c.sykt || <span className="text-muted">—</span>}</CTableDataCell>
                      <CTableDataCell>{c.daerah || <span className="text-muted">—</span>}</CTableDataCell>
                      <CTableDataCell>{c.date}</CTableDataCell>
                      <CTableDataCell onClick={e => e.stopPropagation()}>
                        <CButton size="sm" color="success" variant="ghost" onClick={() => navigate(`/customers/${c.uid}/edit`)} title="Edit">
                          <CIcon icon={cilPencil} />
                        </CButton>
                      </CTableDataCell>
                    </CTableRow>
                  ))}
                  {customers.length === 0 && (
                    <CTableRow>
                      <CTableDataCell colSpan={6} className="text-center text-muted py-4">No customers found.</CTableDataCell>
                    </CTableRow>
                  )}
                </CTableBody>
              </CTable>

              {totalPages > 1 && <PaginationBar page={page} totalPages={totalPages} setPage={setPage} />}
            </>
          )}
        </CCardBody>
      </CCard>
    </>
  );
}
